// TODO: Edit and Reuse AppointmentDetails component instead of this component
import { ActionIcon, Button, Tooltip } from '@mantine/core';
import { Pencil, Trash2, TriangleAlertIcon } from 'lucide-react';
import { motion } from 'motion/react';
import { FC, ReactElement, useMemo } from 'react';

import { BookingCommunicationAction } from '@/components/AppointmentScheduler/booking-communication-action.tsx';
import CircularLoader from '@/components/CircularLoader.tsx';
import Icon from '@/components/Icon.tsx';
import OverlaySection from '@/components/OverlaySection.tsx';
import PriorityMarker from '@/components/PriorityMarker.tsx';
import Paragraph from '@/components/text/Paragraph.tsx';

import { DayjsUnit } from '@/models/Dayjs.ts';
import { AppointmentBookStatus, Insurance, Priority } from '@/models/appointment/Appointment.ts';
import {
    EnrichedAppointmentType,
    Practitioner,
    UnMappedScheduleType,
} from '@/models/appointment/EnrichedPractitionerData.ts';

import { useBookingStore } from '@/stores/bookingStore.ts';

import { getAppointmentFromBatch } from '@/utils/appointmentUtils.ts';
import { dayjs } from '@/utils/dayjsSetup.ts';

// TODO: reuse an existing type
export interface CollapsedAppointmentData {
    is_mapped: boolean;
    practitioner: Practitioner;
    type: EnrichedAppointmentType | UnMappedScheduleType;
    date_time: string;
    duration: number;
    priority: Priority;
    insurance: Insurance;
    description: string;
    info: string;
}

interface Props {
    appointment?: CollapsedAppointmentData;
    onEdit: () => void;
    appointmentCount?: string;
    onDelete: () => void;
    disableEdit: boolean;
    disableCancel: boolean;
    batchId?: string;
    appointmentId?: string;
    onCheckIn?: () => void;
}

const CollapsedAppointment: FC<Props> = ({
    appointment,
    onEdit,
    appointmentCount,
    onDelete,
    disableEdit,
    disableCancel,
    batchId,
    appointmentId,
    onCheckIn,
}) => {
    const formatted_date = useMemo(() => dayjs(appointment?.date_time).format('DD.MM.YY'), [appointment?.date_time]);
    const formatted_time = useMemo(() => dayjs(appointment?.date_time).format('HH:mm'), [appointment?.date_time]);

    const batches = useBookingStore((state) => state.batches);
    const bookedAppointment =
        batchId && appointmentId ? getAppointmentFromBatch(batches, batchId, appointmentId) : undefined;
    const currentAppointmentStatus: AppointmentBookStatus | undefined = bookedAppointment?.status;

    const getActionComponent = (): ReactElement | undefined => {
        switch (currentAppointmentStatus) {
            case AppointmentBookStatus.PENDING_BOOKING: {
                return <CircularLoader />;
            }
            case AppointmentBookStatus.BOOKED: {
                return (
                    <>
                        {bookedAppointment && <BookingCommunicationAction bookedAppointment={bookedAppointment} />}
                        {dayjs(appointment?.date_time).isSame(dayjs(), DayjsUnit.DAY) && (
                            <Button onClick={onCheckIn}>Check-In</Button>
                        )}
                    </>
                );
            }
            case AppointmentBookStatus.DECLINED: {
                // TODO: implement a fix button and workflow
                return <TriangleAlertIcon color="red" />;
            }
            default: {
                // Still in draft, appointment not submitted yet.
                return (
                    <>
                        {!disableCancel && (
                            <ActionIcon
                                variant="transparent"
                                aria-label="Delete Appointment"
                                onClick={onDelete}
                                className="center"
                            >
                                <Icon icon={Trash2} />
                            </ActionIcon>
                        )}

                        {/* TODO: i18n + correct test on tooltip */}
                        <Tooltip disabled={!disableEdit} label="Finish to edit the open form 1st">
                            <ActionIcon
                                disabled={disableEdit}
                                variant="transparent"
                                aria-label="Edit Appointment"
                                onClick={onEdit}
                                className="center disabled:bg-transparent disabled:opacity-40"
                            >
                                <Icon icon={Pencil} color="text-black" />
                            </ActionIcon>
                        </Tooltip>
                    </>
                );
            }
        }
    };

    const action_component = getActionComponent();

    if (!appointment) {
        return <div>No Data available</div>;
    }

    return (
        <OverlaySection className="relative overflow-hidden shadow-sm">
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2, delay: 0.3 }}
                className="w-full"
            >
                <div className="absolute right-0 top-0">
                    <PriorityMarker priority={appointment.priority} />
                </div>
                <div className="flex w-full items-center justify-between">
                    <div className="flex flex-col gap-2">
                        <div className="flex items-center gap-2">
                            <Paragraph className="text-lg text-slate-500">
                                {`(${appointment.insurance}) ${appointment.type.name} - ${appointment.duration} min`}
                            </Paragraph>
                            {appointmentCount && (
                                <Paragraph className="inline-block w-auto whitespace-nowrap text-sm text-slate-500">
                                    {appointmentCount}
                                </Paragraph>
                            )}
                        </div>
                        <Paragraph>
                            {/*TODO: use practitioner name*/}
                            {formatted_date} um {formatted_time} mit {appointment.practitioner.name}
                        </Paragraph>
                        {appointment.description && (
                            <div className="text-sm text-slate-500" title="Bezeichnung">
                                {appointment.description}
                            </div>
                        )}
                        {appointment.info && (
                            <div className="text-sm italic text-slate-400" title="Termin Info">
                                {appointment.info}
                            </div>
                        )}
                    </div>
                    <div className="flex items-center gap-2">{action_component}</div>
                </div>
            </motion.div>
        </OverlaySection>
    );
};

export default CollapsedAppointment;
